<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import UserMenuItems from '@/components/user/header/UserMenuItems.vue';

type Props = {
  buttonsAddClass?: string;
};

const { buttonsAddClass = 'default' } = defineProps<Props>();

const { userCanOnlyWithdraw } = useUserLimits();

const { isGamificationLevelsActived, isGamificationProfileActived } =
  storeToRefs(useSmarticoConfigs());

const menuDiv = ref<HTMLDivElement | null>(null);
const isMenuOpened = ref<boolean>(false);

function toggleMenu() {
  isMenuOpened.value = !isMenuOpened.value;
}

function closeMenu() {
  isMenuOpened.value = false;
}

onClickOutside(menuDiv, () => closeMenu());
</script>

<template>
  <HeadlessMenu
    ref="menuDiv"
    as="div"
    class="relative inline-block text-left z-50 h-[30px] lg:h-[40px] lg:w-[40px]"
  >
    <HeadlessMenuButton
      v-if="
        (isGamificationProfileActived || isGamificationLevelsActived) &&
        !userCanOnlyWithdraw
      "
      aria-label="Open User Menu"
      @click="toggleMenu"
    >
      <UserHeaderApostatudoGamificationButton />
    </HeadlessMenuButton>

    <HeadlessMenuButton
      v-else
      aria-label="Open User Menu"
      :class="[$style.btnTrigger, $style[buttonsAddClass], appNameLower()]"
      @click="toggleMenu"
    >
      <span>
        <NuxtIcon name="user/user_circle" />
      </span>
    </HeadlessMenuButton>

    <HeadlessMenuItems
      v-if="isMenuOpened"
      :class="$style.menu"
      static
    >
      <div :class="$style.innerMenu">
        <UserMenuItems
          is-menu
          :style-module="$style"
          @close="closeMenu"
        />
      </div>
    </HeadlessMenuItems>
  </HeadlessMenu>
</template>

<style module lang="postcss">
.menu {
  @apply absolute p-5 gap-5 right-0 lg:-mt-0.5 w-[354px] origin-top-right rounded-[20px] shadow-lg bg-bg-primary border-solid border-2 border-white;
  &:before {
    content: '';
    @apply absolute bottom-full -translate-x-1/2 right-3 rounded-sm border-solid border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-white;
  }
}

.innerMenu {
  @apply w-full overflow-y-auto overflow-x-hidden;
  max-height: calc(100vh - 130px);
  @media (max-width: 1023px) {
    max-height: calc(100vh - 175px);
  }
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
}

.btnTrigger {
  @apply w-[39.19px] relative h-[30px] flex justify-center items-center px-2 text-xl text-primary rounded bg-header-links/10 overflow-visible xl:text-2xl;
  span {
    @apply relative z-10 text-primary;
  }

  .notifyBadge {
    @apply absolute -top-1.5 -right-1.5 bg-error text-white rounded-full text-[.49rem] font-semibold w-[14px] h-[14px] flex items-center justify-center z-40;
  }

  &.new {
    @apply rounded-3xl w-[28px] lg:w-[34px] p-0;
  }
  &.gamificationActive {
    @apply bg-transparent max-h-[60px] w-auto rounded-none;

    .notifyBadge {
      @apply -top-[3px] -right-[3px];
    }
  }
}

.item {
  @apply cursor-pointer w-full flex items-center rounded-md px-5 py-2 text-sm font-semibold normal-case text-black/80;

  &.logout {
    @apply text-black/40;
  }

  &.isActive {
    @apply bg-primary/20;

    &.logout {
      @apply bg-error/10 text-error;
    }
  }

  .count {
    @apply ml-2 bg-error text-white rounded-full text-[.5rem] font-semibold w-4 h-4 flex items-center justify-center;
  }
}

.icon {
  @apply mr-3 h-4 w-4;

  svg {
    @apply w-full h-full;
  }
}

.separator {
  @apply my-2;

  border-top: 1px solid #ededed;
}
</style>
